import type React from 'react';

import { logger } from '../../logger';
import { type QuantitativeSize, type QuantitativeSizeString } from '../../types/sizes';
import { parseQuantitativeSize, quantitativeSizeToString } from '../../util/sizes';

import * as constants from './constants';
import {
  ButtonDefaultSize,
  ButtonDiscreteSize,
  type ButtonSize,
  type ButtonVariant,
  isButtonDiscreteSize,
  isLinkOrInlineLinkVariant,
} from './types';

/**
 * Validates and optionally manipulates the numeric value represented by the provided, quantitative
 * button height, {@link QuantitativeSize<'px'>}, returning fallback values if the provided
 * height is negative, non-finite, NaN or an invalid string representation of a pixel height value.
 *
 * @param {QuantitativeSize<'px'>} height
 *   The quantitative size used to render the {@link Button}'s height.
 *
 * @returns {SanitizedHeight<P>}
 *   Either a string, pixel representation of the provided height that may be optionally
 *   manipulated based on the provided parameters, or the fallback value/undefined if the provided
 *   height is invalid.
 */
const validateHeight = (height: QuantitativeSize<'px'>): number | undefined => {
  if (typeof height === 'number') {
    if (!isFinite(height) || isNaN(height)) {
      logger.warn(
        `The value provided for the button size, '${height}', could not be represented in a ` +
          'numeric form because the value is either not finite or NaN.',
      );
      return undefined;
    }
    if (height < 0) {
      logger.warn(
        `The value provided for the button size, '${height}', could not be represented in a ` +
          'numeric form because the value is negative.',
      );
      return undefined;
    }
    return height;
  }
  const [numericSize] = parseQuantitativeSize(height, { strict: false });
  if (numericSize === null) {
    logger.warn(
      `The value provided for the button size, '${height}', could not be represented in a ` +
        'numeric form because the value is invalid.',
    );
    return undefined;
  }
  return validateHeight(numericSize);
};

const LineHeightOffset = 2;

const _parseButtonLineHeight = (height: QuantitativeSize<'px'>, verticalPadding?: number) => {
  const validated = validateHeight(height);
  if (validated === undefined) {
    return undefined;
  }
  if (verticalPadding === undefined) {
    return quantitativeSizeToString(Math.max(validated - LineHeightOffset, 0), 'px');
  }
  const overallOffset = 2 * verticalPadding + LineHeightOffset;
  return quantitativeSizeToString(Math.max(validated - overallOffset, 0), 'px');
};

/**
 * Determines the line-height of the {@link Button} based on the provided size, {@link ButtonSize},
 * and variant, {@link ButtonVariant}.
 *
 * In order for the text inside of the {@link Button} elements to be vertically centered with equal
 * spacing above and below the text, it is important that concrete, quantitative values are used for
 * the line-height of the text in the {@link Button} (such as 24px) rather than relative
 * line-heights (such as 1.75) that MUI defines unless overridden.  In order to do this, the
 * line-height is set so that it is just slightly less than the height of the {@link Button}.
 *
 * In cases where the combination of the size, {@link ButtonSize}, and variant,
 * {@link ButtonVariant}, cannot be used to determine a finite, quantitative line-height,
 * {@link QuantitativeSizeString<'px'>} (or an inherited line-height, 'inherit', in cases where
 * the variant is "inline-link") the method will return undefined - which will cause the default,
 * MUI-defined, relative line-height to be used instead.
 *
 * The only cases where the method will return undefined, causing the MUI-defined, relative
 * line-height to be used, are when
 *
 * 1. The 'size' being used to render the {@link Button} is "auto".
 * 2. The 'size' being used to render the {@link Button} is invalid (it is negative, non-finite or
 *    NaN).
 *
 * @param {ButtonSize} size The size that the {@link Button} is being rendered with.
 * @param {ButtonVariant} variant
 *   The variant that the {@link Button} is being rendered with.  This is needed so that the
 *   line-height is set to "inherit" for the "inline-link" variant.
 *
 * @returns {'inherit' | QuantitativeSizeString<'px'> | undefined}
 *   A valid CSS value for the 'line-height' of the {@link Button}, or undefined if the provided
 *   size is "auto" or results in a negative, non-finite or NaN numeric value.
 */
export const parseButtonLineHeight = (
  size: ButtonSize,
  variant: ButtonVariant,
): 'inherit' | QuantitativeSizeString<'px'> | undefined => {
  if (variant === 'inline-link') {
    return 'inherit';
  } else if (isButtonDiscreteSize(size)) {
    return _parseButtonLineHeight(
      constants.ButtonSizeProperties[size].height,
      constants.ButtonSizeProperties[size].padding[0],
    );
  } else if (size === 'auto') {
    return undefined;
  }
  return _parseButtonLineHeight(size);
};

interface ParseButtonHeightOptions {
  readonly as?: 'number' | 'string';
}

type WithAuto<V, T> = V extends 'auto' ? 'auto' : V extends ButtonDiscreteSize ? T : null | T;

type ParsedButtonHeight<V extends ButtonSize, O extends ParseButtonHeightOptions> = WithAuto<
  V,
  O extends {
    readonly as: 'number';
  }
    ? number
    : QuantitativeSizeString<'px'>
>;

const safelyCoerceAndReturnHeight =
  <O extends ParseButtonHeightOptions>() =>
  <V extends ButtonSize, O2 extends ParseButtonHeightOptions>(
    height: ParsedButtonHeight<ButtonDiscreteSize | QuantitativeSize<'px'>, O>,
  ): ParsedButtonHeight<V, O2> =>
    height as ParsedButtonHeight<V, O2>;

/**
 * Parses the provided size, {@link ButtonSize}, returning either a pixel-valued representation
 * of the height or 'auto' in the case that the provided size is 'auto'.
 *
 * In the case that the provided size, {@link V}, is not 'auto', but is either an invalid numeric
 * value (negative, non-finite or NaN) or an invalid string representation of a pixel height value,
 * the function will return null rather than a pixel-valued representation of the height as a
 * fallback.
 *
 * However, if the provided size is a discrete size, {@link ButtonDiscreteSize}, and the associated
 * height value for that size is invalid, the function will throw an error, as that
 * would indicate a problem with the constant defined height mappings rather than a dynamic
 * prop.
 *
 * @throws {Error}
 *   Throws if the provided size is a discrete size, {@link ButtonDiscreteSize}, and the associated
 *   height value for that size is invalid.
 */
export const parseButtonHeight = <V extends ButtonSize, O extends ParseButtonHeightOptions>(
  size: V,
  options?: O,
): ParsedButtonHeight<V, O> => {
  if (size === 'auto') {
    const result: ParsedButtonHeight<'auto', O> = 'auto';
    return result as ParsedButtonHeight<V, O>;
  }
  if (isButtonDiscreteSize(size)) {
    const height = constants.ButtonSizeProperties[size].height;
    const validated = validateHeight(height);
    if (validated === undefined) {
      throw new Error(
        `The height value, '${height}', associated with the provided button size, ` +
          `'${size}', is invalid.`,
      );
    }
    if (options?.as === 'number') {
      return safelyCoerceAndReturnHeight<{ readonly as: 'number' }>()<V, O>(validated);
    }
    return safelyCoerceAndReturnHeight<{ readonly as: 'string' }>()<V, O>(
      quantitativeSizeToString(validated, 'px'),
    );
  }
  const numeric = parseQuantitativeSize(size, { strict: false })[0];
  if (numeric === null) {
    logger.warn(
      `The value provided for the button size, '${size}', could not be represented in a ` +
        'numeric form because the value is invalid.',
    );
    const result: ParsedButtonHeight<QuantitativeSize<'px'>, O> = null;
    return result as ParsedButtonHeight<V, O>;
  }
  const validated = validateHeight(size);
  if (validated === undefined) {
    const result: ParsedButtonHeight<QuantitativeSize<'px'>, O> = null;
    return result as ParsedButtonHeight<V, O>;
  }
  if (options?.as === 'number') {
    return safelyCoerceAndReturnHeight<{ readonly as: 'number' }>()<V, O>(validated);
  }
  return safelyCoerceAndReturnHeight<{ readonly as: 'string' }>()<V, O>(
    quantitativeSizeToString(validated, 'px'),
  );
};

export const parseButtonFontSize = (size: ButtonSize): QuantitativeSizeString<'px'> => {
  const sz = isButtonDiscreteSize(size) ? size : ButtonDefaultSize;
  return quantitativeSizeToString(constants.ButtonSizeProperties[sz].fontSize, 'px');
};

export const parseButtonIconSize = (size: ButtonSize): QuantitativeSizeString<'px'> => {
  const sz = isButtonDiscreteSize(size) ? size : ButtonDefaultSize;
  return quantitativeSizeToString(constants.ButtonSizeProperties[sz].iconSize, 'px');
};

export const parseButtonIconGap = (size: ButtonSize): QuantitativeSizeString<'px'> => {
  const sz = isButtonDiscreteSize(size) ? size : ButtonDefaultSize;
  return quantitativeSizeToString(constants.ButtonSizeProperties[sz].iconGap, 'px');
};

export const parseButtonPadding = (size: ButtonSize): string => {
  const sz = isButtonDiscreteSize(size) ? size : ButtonDefaultSize;
  return [
    quantitativeSizeToString(constants.ButtonSizeProperties[sz].padding[0], 'px'),
    quantitativeSizeToString(constants.ButtonSizeProperties[sz].padding[1], 'px'),
  ].join(' ');
};

/**
 * Returns the {@link SxProps} style object that depends on the size, {@link ButtonSize}, and
 * variant, {@link ButtonVariant}, that the button is being rendered with.
 *
 * The 'line-height' will only be included in the returned value if a finite, quantitative value for
 * the 'line-height' could be determined.  If it could not, the 'line-height' will be omitted,
 * and the 'line-height' of the {@link Button} will fallback to the MUI-defined, relative
 * line-height.
 *
 * @see {parseButtonLineHeight}
 */
export const getButtonSizeSx = (size: ButtonSize, variant: ButtonVariant) => {
  const baseSx: React.CSSProperties = {
    fontSize: parseButtonFontSize(size),
    height: parseButtonHeight(size) ?? 'auto',
    padding: isLinkOrInlineLinkVariant(variant) ? 0 : parseButtonPadding(size),
  };
  const lineHeight = parseButtonLineHeight(size, variant);
  if (lineHeight === undefined) {
    return baseSx;
  }
  return { ...baseSx, lineHeight };
};

type IconButtonPaddingSpec = {
  readonly padding: number;
  readonly range: [ButtonDiscreteSize | number, ButtonDiscreteSize | number];
};

const IconButtonPaddingMap: IconButtonPaddingSpec[] = [
  { padding: 1, range: [0, 22] },
  { padding: 2, range: [22, ButtonDiscreteSize.XSmall] },
  {
    padding: 4,
    range: [ButtonDiscreteSize.XSmall, ButtonDiscreteSize.Small],
  },
  {
    padding: constants.DefaultIconButtonPadding,
    range: [ButtonDiscreteSize.Small, Infinity],
  },
];

const translateRangeToNumeric = (range: IconButtonPaddingSpec['range']): [number, number] => [
  typeof range[0] === 'number' ? range[0] : parseButtonHeight(range[0], { as: 'number' }),
  typeof range[1] === 'number' ? range[1] : parseButtonHeight(range[1], { as: 'number' }),
];

export const parseIconButtonPadding = (
  size: ButtonDiscreteSize | QuantitativeSize<'px'>,
): number => {
  const height = parseButtonHeight(size, { as: 'number' });
  if (height === null) {
    return constants.DefaultIconButtonPadding;
  }
  for (const { padding, range } of IconButtonPaddingMap) {
    const numericRange = translateRangeToNumeric(range);
    if (height >= numericRange[0] && height < numericRange[1]) {
      return padding;
    }
  }
  throw new Error(
    `The provided size '${size}' did not match any of the expected size ranges. This indicates ` +
      'faulty logic in the definition of the IconButtonPaddingMap or in the ' +
      'parseIconButtonPadding function.',
  );
};
